import { Component, Inject, OnInit } from '@angular/core';
import { AuthenticationService, UserService } from '../../../../core/services';
import { OauthProvider } from '../../../../oauth/providers';
import { CommonUtilService, RequestBodyService } from '../../../services';
import { ReConnectSettingComponent } from '../../re-connect-setting/re-connect-setting.component';
import { AccountAttributesEnum } from '../../../enums';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DynamicsApiService } from '../../../services/api';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { getErrorMessage } from 'src/app/shared/helpers';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { FilterPipeModule, OrderByPipeModule } from '../../../pipes';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { SiModalModule } from '../../si-modal';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import {
  DuplicateResolutionRadioGroupModule
} from '../../ui-components/duplicate-resolution-group/duplicate-resolution-radio-group.module';

@UntilDestroy()
@Component({
    selector: 'app-dynamics',
    templateUrl: './dynamics.component.html',
    styleUrls: ['./../export.component.scss'],
    imports: [
      CommonModule,
      MatProgressSpinnerModule,
      FormsModule,
      ReactiveFormsModule,
      FontAwesomeModule,
      MatInputModule,
      MatFormFieldModule,
      MatExpansionModule,
      FilterPipeModule, OrderByPipeModule, SiModalModule,
      DuplicateResolutionRadioGroupModule,
      MatButtonModule
    ],
    standalone: true,
})
export class DynamicsComponent implements OnInit {
    pages;
    leadOwners;
    campaigns;
    exportType = '';
    generalSettings;
    showPreviousButton = false;
    showNextButton = false;
    currentPage;
    initialPage;
    selectedLeadOwner;
    selectedCampaigns = [];
    contacts = [];
    companyData = [];
    icpIntelJobUUID: string = null;
    duplicateLeadsMatchIds = {};
    duplicateContactsMatchIds = [];
    duplicateLeadsCount = 0;
    duplicateContactsCount = 0;
    duplicateMatchIds;
    duplicateCount = 0;
    duplicateResolution = 'update';
    exportedContactCount = 0;
    busyLabel = 'Loading ...';
    busySublabel = '';
    isBusy = false;
    showCloseButton = false;
    showCancelButton = true;
    showRetryButton = false;
    failedContacts: FailedContactModel[] = [];
    duplicateFailedContacts: FailedContactModel[] = [];
    exportError = '';
    filterLeadOwner = '';
    filterCampaigns = '';
    pageArray;
    duplicateCompanyMatchIds = [];
    duplicateCompanyCount = 0;
    exportedCompanyCount = 0;
    failedCompany = [];
    isDynamicsComponentOpened: boolean = true;
    accuracyTier: string;
    accuracyTierText: string;
    readonly accountAttributesEnum: typeof AccountAttributesEnum = AccountAttributesEnum;

    constructor(
        private commonUtilService: CommonUtilService,
        private authenticationService: AuthenticationService,
        private readonly matDialog: MatDialog,
        private readonly matDialogRef: MatDialogRef<DynamicsComponent>,
        private requestBodyService: RequestBodyService,
        private dynamicsApiService: DynamicsApiService,
        private readonly userService: UserService,
        @Inject(MAT_DIALOG_DATA) public readonly data: any,
    ) {
        this.icpIntelJobUUID = this.data['icpIntelJobUUID'] || null;

        if (this.data['contacts']) {
            this.contacts = this.data['contacts'];
            this.pages = {
                loading: 0,
                exportType: 1,
                leadOwner: 2,
                campaign: 3,
                duplicates: 4,
                export: 5,
                exportFinished: 6,
                exportEmpty: 7,
                exportFailed: 8,
                requiresReauth: 9
            };
            this.accuracyTier = this.data['accuracyTier'];
            this.accuracyTierText = this.data['accuracyTierText'];
        } else if (this.data['company']) {
            this.companyData = this.data['company'];
            this.pages = {
                loading: 0,
                duplicates: 1,
                export: 2,
                exportFinished: 3,
                exportEmpty: 4,
                exportFailed: 5,
                requiresReauth: 6
            };
        }
    }

    ngOnInit() {
        this.initializeFirstStep();
    }

    initializeFirstStep() {
        this.currentPage = this.pages.loading;
        this.initialPage = this.currentPage;
        this.dynamicsApiService.validateMapping().pipe(untilDestroyed(this)).subscribe((mappingData) => {
            if (Object.values(mappingData).every(item => item === true)) {
                this.getSettings();
            } else {
                const sectionsInError = [];
                if (this.data['contacts']) {
                    if (!mappingData.leadFields) {
                        sectionsInError.push('Lead Mappings');
                    }
                    if (!mappingData.contactFields) {
                        sectionsInError.push('Contact Mappings');
                    }
                    if (!mappingData.accountFields) {
                        sectionsInError.push('Company Mappings');
                    }
                    if (this.userService.hasAccess(this.accountAttributesEnum.TECHEXPORTS)) {
                        if (!mappingData.technoLeadFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                        if (!mappingData.technoContactFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                        if (!mappingData.technoAccountFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                    }
                } else {
                    if (!mappingData.accountFields) {
                        sectionsInError.push('Company Mappings');
                    }
                    if (this.userService.hasAccess(this.accountAttributesEnum.TECHEXPORTS) && !mappingData.technoAccountFields) {
                        sectionsInError.push('Technographics Mappings');
                    }
                }
                if (sectionsInError.length > 0) {
                    sectionsInError.forEach(section => {
                        this.commonUtilService.showToastWithError(
                            `One or more fields in your ${section} are no longer available. Update your ${section}.`
                        );
                    });
                }
                this.matDialogRef.close();
            }
        }, error => {
            this.dynamicsApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(data => {
                this.initializeFirstStep();
            }, refreshError => {
                this.errorRefreshToken();
            });
        });
    }

    errorRefreshToken() {
        if (this.isDynamicsComponentOpened) {
            this.onClose();
            const modalConfig: MatDialogConfig = {
                panelClass: 'modal-lg',
                data: {},
            };
            if (this.data['contacts']) {
                modalConfig.data['provider'] = OauthProvider.Dynamics;
                modalConfig.data['match_ids'] = this.contacts;
            } else {
                modalConfig.data['provider'] = OauthProvider.DynamicsCompany;
                modalConfig.data['match_ids'] = this.companyData;
                modalConfig.data['ifFromList'] = this.commonUtilService.ifFromList;

            }
            this.matDialog.open(ReConnectSettingComponent, modalConfig);
        }
    }

    getSettings() {
        this.dynamicsApiService.getSettings().pipe(untilDestroyed(this)).subscribe((response) => {
            this.generalSettings = response.general;
            if (this.data['contacts']) {
                this.loadLeadOwners();
            } else if (this.data['company']) {
                this.validateCompanyDuplicates();
            }
        }, error => {
            if (error.status === 404 && error.error?.message) {
                this.commonUtilService.showToastWithError(getErrorMessage(error));
                this.onClose();
                return;
            }
            this.dynamicsApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(data => {
                this.ngOnInit();
            }, refreshError => {
                this.errorRefreshToken();
            });
        });
    }

    loadLeadOwners() {
        this.dynamicsApiService.loadLeadOwners().pipe(untilDestroyed(this)).subscribe((response) => {
            const userEmail = this.authenticationService.getEmailAndName('dyEmail', 'dyDisplayName')['email'].toLowerCase();
            this.leadOwners = response;
            this.leadOwners.forEach((element) => {
                if (element.email === userEmail) {
                    if (this.generalSettings.dyDefaultOwnerIsConnectedUser === true) {
                        this.selectedLeadOwner = element.id;
                    }
                }
            });
            if (this.generalSettings.dyAllowExportAsLead === true && this.generalSettings.dyAllowExportAsContact === false && this.generalSettings.dyPromptForCampaigns === true) {
                this.loadCampaigns();
            } else {
                this.updatePageSequence();
            }
        }, (error) => {
            this.dynamicsApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(data => {
                this.initializeFirstStep();
            }, refreshError => {
                this.errorRefreshToken();
            });
        });
    }

    loadCampaigns() {
        this.dynamicsApiService.loadCampaigns().pipe(untilDestroyed(this)).subscribe((response) => {
            this.campaigns = response;
            this.updatePageSequence();
        }, error => {
            this.dynamicsApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(data => {
                this.initializeFirstStep();
            }, refreshError => {
                this.errorRefreshToken();
            });
        });
    }

    updatePageSequence() {
        if (!this.exportType) {
            if (this.generalSettings.dyAllowExportAsContact === true) {
                this.exportType = 'contact';
            }

            if (this.generalSettings.dyAllowExportAsLead === true) {
                this.exportType = 'lead';
            }
        }
        this.pageArray = this.pages;

        Object.keys(this.pageArray).forEach((currentPage) => {
            if (this.pageArray[currentPage] === this.pageArray.exportType && (this.generalSettings.dyAllowExportAsLead === false || this.generalSettings.dyAllowExportAsContact === false)) {
                delete this.pageArray[currentPage];
            }

            if (this.generalSettings.dyPromptForCampaigns === false && this.pageArray[currentPage] === this.pageArray.campaign) {
                delete this.pageArray[currentPage];
            }
            if (this.generalSettings.dyPromptForLeadOwners === false && this.pageArray[currentPage] === this.pageArray.leadOwner) {
                delete this.pageArray[currentPage];
            }
        });
        // for (const currentPage in this.pageArray) {
        //     if (this.pageArray[currentPage] === this.pageArray.exportType && (this.generalSettings.dyAllowExportAsLead === false || this.generalSettings.dyAllowExportAsContact === false)) {
        //         delete this.pageArray[currentPage];
        //     }

        //     if (this.generalSettings.dyPromptForCampaigns === false && this.pageArray[currentPage] === this.pageArray.campaign) {
        //         delete this.pageArray[currentPage];
        //     }
        //     if (this.generalSettings.dyPromptForCampaigns === true && this.exportType === 'contact' && this.pageArray[currentPage] === this.pageArray.campaign) {
        //         delete this.pageArray[currentPage];
        //     }
        //     if (this.generalSettings.dyPromptForLeadOwners === false && this.pageArray[currentPage] === this.pageArray.leadOwner) {
        //         delete this.pageArray[currentPage];
        //     }
        // }
        // Reordering
        Object.entries(this.pageArray).forEach((key, index) => {
            this.pageArray[key[0]] = index;
        });
        this.onNext();
    }

    onNext() {
        this.currentPage++;
        if (this.data['contacts']) {
            if (this.currentPage === this.pageArray.campaign) {
                if (this.exportType === 'contact') {
                    this.onNext();
                    return;
                }
                this.filterCampaigns = '';
            }
            this.executeCurrentStep();
        } else if (this.data['company']) {
            this.executeCurrentStepCompany();
        }
    }

    onPrevious() {
        if (this.data['contacts']) {
            let page = this.currentPage;
            page = page - 1;
            if (this.currentPage === this.pageArray.exportEmpty) {
                if (this.generalSettings.dyDuplicateResolution !== 'prompt') {
                    page = this.pageArray.duplicates - 1;
                } else {
                    page = this.pageArray.duplicates;
                }
            }
            this.currentPage = page;
            if (this.currentPage === this.pageArray.campaign && this.exportType === 'contact') {
                this.onPrevious();
                return;
            }
            this.executeCurrentStep();
        } else if (this.data['company']) {
            if (this.currentPage === this.pages.exportEmpty) {
                this.currentPage = this.pages.duplicates;
            } else {
                this.currentPage = this.currentPage--;
            }
            this.executeCurrentStepCompany();
        }
    }

    onClose() {
        this.isDynamicsComponentOpened = false;
        this.matDialogRef.close();
    }

    setExportType(type) {
        this.exportType = type.toLowerCase();
        if (this.generalSettings.dyPromptForCampaigns === true) {
            this.loadCampaigns();
        } else {
            this.onNext();
        }
    }
    executeCurrentStepCompany() {
        if (this.currentPage === this.initialPage) {
            this.showPreviousButton = false;
            this.showCloseButton = false;
        } else if (this.currentPage === this.pages.duplicates) {
            this.showPreviousButton = false;
            this.showNextButton = true;
            this.showCloseButton = false;
        } else if (this.currentPage === this.pages.export) {
            this.showPreviousButton = true;
            this.showNextButton = true;
            this.showRetryButton = false;
            this.showCloseButton = false;
            this.exportCompanies();
        } else if (this.currentPage === this.pages.exportEmpty) {
            this.showCancelButton = false;
            this.showPreviousButton = true;
            this.showNextButton = false;
            this.showCloseButton = true;
        } else if (this.currentPage === this.pages.exportFinished) {
            this.showCancelButton = false;
            this.showPreviousButton = false;
            this.showNextButton = false;
            this.showCloseButton = true;
        }
    }
    executeCurrentStep() {
        if (this.currentPage === this.pageArray.duplicates) {
            this.showCloseButton = false;
            this.validateDuplicates();
        } else if (this.currentPage === this.pageArray.export) {
            this.showCloseButton = false;
            this.export();
        } else if (this.currentPage === this.pageArray.exportEmpty) {
            if ((parseInt(this.pageArray.duplicates, 10) - 1) !== 0 && (parseInt(this.pageArray.duplicates, 10) - 1) !== undefined) {
                this.showPreviousButton = true;
            } else {
                this.showPreviousButton = false;
                this.showCancelButton = false;
            }
            this.showCloseButton = true;
            this.showNextButton = false;
        } else if (this.currentPage === this.pageArray.leadOwner || this.currentPage === this.pageArray.campaign) {
            this.showPreviousButton = (this.currentPage - 1) !== 0;
            this.showNextButton = true;
            this.showCloseButton = false;
        } else if (this.currentPage === this.pageArray.exportFinished) { // Last Page
            this.showNextButton = false;
            this.showPreviousButton = false;
            this.showCancelButton = false;
            this.showCloseButton = true;
        } else if (this.currentPage === this.pageArray.exportType) { // First Page
            this.showPreviousButton = false;
            this.showCloseButton = false;
            this.showNextButton = false;
        }
    }

    selectLeadOwner(id) {
        if (this.selectedLeadOwner === id) {
            this.selectedLeadOwner = null;
        } else {
            this.selectedLeadOwner = id;
        }
    }

    selectCampaign(cid) {
        const idx = this.selectedCampaigns.indexOf(cid);
        if (idx === -1) {
            this.selectedCampaigns.push(cid);
        } else {
            this.selectedCampaigns.splice(idx, 1);
        }
    }

    isCampaignSelected(cid) {
        return this.selectedCampaigns.indexOf(cid) !== -1;
    }

    validateCompanyDuplicates() {
        this.onNext();
        this.busyLabel = 'Validating Duplicates ...';
        this.busySublabel = 'Depending on the number of company, this could take a few minutes.';
        this.isBusy = true;
        const companyIds = this.companyData.map((item) => {
            if (typeof item === 'object') {
                return item.companyId.toString();
            } else {
                return item;
            }
        });
        const requestBody = {
            company_ids: companyIds,
            verified: this.data['verified'],
            is_international: this.data['international'],
            icpintel_job_uuid: this.icpIntelJobUUID
        };
        this.dynamicsApiService.validateCompaniesDuplicates(requestBody).pipe(untilDestroyed(this)).subscribe((resp: any) => {
            resp.forEach((item) => {
                if (item['duplicate'] === true) {
                    this.duplicateCompanyMatchIds.push(item);
                }
            });
            this.duplicateCompanyCount = this.duplicateCompanyMatchIds.length;
            if (this.duplicateCompanyCount === 0) {
                this.onNext();
            } else if (this.generalSettings.dyDuplicateResolution !== 'prompt') {
                this.duplicateResolution = this.generalSettings.dyDuplicateResolution;
                this.onNext();
            } else {
                this.isBusy = false;
            }
        }, (error) => {
            const invalidGrant: boolean = error.status === 400 && error.error?.errors?.[0]?.code === 'invalid_grant';
            if (error.status === 401 || invalidGrant) {
                this.errorRefreshToken();
                return;
            }

            this.commonUtilService.showToastWithError(error.error?.message || error.data?.message);
            this.onClose();
        });
    }
    validateDuplicates() {
        this.busyLabel = 'Validating Duplicates ...';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        const matchIds = this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return contact.matchId;
            } else {
                return contact;
            }
        });
        this.dynamicsApiService.validateDuplicates(this.exportType, matchIds, this.icpIntelJobUUID).pipe(untilDestroyed(this)).subscribe((response: any) => {
            const dupes = [];
            let dupesCount = 0;
            const dupeLeads = [];
            const dupeContacts = [];
            let dupeLeadsCount = 0;
            let dupeContactsCount = 0;
            if ('leads' in response) {
                response.leads.forEach((item) => {
                    if (item['duplicate'] === true) {
                        dupesCount++;
                        dupeLeads.push(item);
                        dupes.push(item);
                    }
                });
                dupeLeadsCount = dupeLeads.length;
            }
            if ('contacts' in response) {
                response.contacts.forEach((item) => {
                    if (item['duplicate'] === true) {
                        dupesCount++;
                        dupeContacts.push(item);
                        dupes.push(item);
                    }
                });
                dupeContactsCount = dupeContacts.length;
            }
            this.duplicateLeadsCount = dupeLeadsCount;
            this.duplicateContactsCount = dupeContactsCount;
            this.duplicateLeadsMatchIds = dupeLeads;
            this.duplicateContactsMatchIds = dupeContacts;
            this.duplicateMatchIds = dupes;
            this.duplicateCount = dupesCount;
            if (dupesCount === 0) {
                this.onNext();
            } else {
                if (this.generalSettings.dyDuplicateResolution !== 'prompt') {
                    this.duplicateResolution = this.generalSettings.dyDuplicateResolution;
                    this.showPreviousButton = false;
                    this.showNextButton = false;
                    this.onNext();
                } else {
                    this.isBusy = false;
                    this.showNextButton = true;
                    if (this.pageArray.duplicates - 1 !== 0) {
                        this.showPreviousButton = true;
                    }
                }
            }
        }, (error) => {
            const invalidGrant: boolean = error.status === 400 && error.error?.errors?.[0]?.code === 'invalid_grant';
            if (error.status === 401 || invalidGrant) {
                this.errorRefreshToken();
                return;
            }

            this.commonUtilService.showToastWithError(error.error?.message || error.data?.message);
            this.onClose();
        });
    }

    getByMatchId(id, name) {
        let Name;
        if (this.contacts.filter((item) => item.matchId === id).length > 0) {
            Name = this.contacts.filter((item) => item.matchId === id)[0][name];
        } else {
            Name = '';
        }
        return Name;
    }
    exportCompanies() {
        const toBeExported = this.companyData.map((item) => {
            if (typeof item === 'object') {
                return item.companyId.toString();
            } else {
                return item.toString();
            }
        });
        // Skip all duplicates
        if (this.duplicateCompanyMatchIds && this.duplicateResolution === 'skip') {
            this.duplicateCompanyMatchIds.forEach((item) => {
                this.commonUtilService.removeMatchId(toBeExported, item, 'company');
            });
        }

        if (toBeExported.length === 0) {
            this.isBusy = false;
            this.currentPage = this.pages.exportEmpty;
            this.executeCurrentStepCompany();
        } else {
            this.exportedCompanyCount = toBeExported.length;
            this.busyLabel = 'Exporting ...';
            this.busySublabel = 'Depending on the number of companies, this could take a few minutes.';
            this.isBusy = true;
            const requestBody = {
                company_ids: toBeExported,
                verified: this.data['verified'],
                is_international: this.data['international'],
                duplicateResolution: this.duplicateResolution === 'skip' ? '' : this.duplicateResolution,
                icpintel_job_uuid: this.icpIntelJobUUID
            };

            this.dynamicsApiService.exportCompany(requestBody).pipe(untilDestroyed(this)).subscribe((response: any) => {
                this.failedCompany = response;
                // Skip all failed company
                if (this.failedCompany && this.failedCompany.length > 0) {
                    this.failedCompany.forEach((failedCompay) => {
                        const companyId = toBeExported.filter((id) => failedCompay?.companyId === id)[0];
                        this.commonUtilService.removeMatchId(toBeExported, companyId, 'company');
                    });
                }
                if (toBeExported.length > 0) {
                    this.authenticationService.updateLoggedUserData();
                    const exportedIds = toBeExported.map((item) => parseInt(item, 10));
                    this.requestBodyService.changeCompanyRevealMatchesID({ 'type': 'dynamics', 'data': exportedIds });
                }
                this.isBusy = false;
                this.onNext();
            }, (error) => {
                const errMsg = getErrorMessage(error, true);
                if (errMsg) {
                    this.exportError = errMsg;
                    this.showRetryButton = true;
                } else {
                    this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
                }
                this.currentPage = this.pages.exportFailed;
                this.showNextButton = false;
                this.showPreviousButton = false;
                this.isBusy = false;
            });
        }
    }
    export() {
        const toBeExported = this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return contact.matchId;
            } else {
                return contact;
            }
        });
        // Skip all duplicates
        if (this.duplicateResolution === 'skip') {
            if (typeof this.duplicateMatchIds === 'object') {
                for (const key in this.duplicateLeadsMatchIds) {
                    if (this.duplicateLeadsMatchIds.hasOwnProperty(key)) {
                        this.commonUtilService.removeMatchId(toBeExported, this.duplicateLeadsMatchIds[key]);
                    }
                }
                for (const key in this.duplicateContactsMatchIds) {
                    if (this.duplicateContactsMatchIds.hasOwnProperty(key)) {
                        this.commonUtilService.removeMatchId(toBeExported, this.duplicateContactsMatchIds[key]);
                    }
                }
            } else {

                this.duplicateMatchIds.forEach((item) => {
                    this.commonUtilService.removeMatchId(toBeExported, item.key);
                });
            }
        }
        if (toBeExported.length === 0) {
            this.isBusy = false;
            this.currentPage = this.pageArray.exportEmpty;
            this.executeCurrentStep();
            return;
        }
        // this.busyLabel = 'Exporting ...';
        this.busyLabel = '';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        this.exportedContactCount = toBeExported.length;
        this.dynamicsApiService.export(this.exportType, this.selectedLeadOwner, this.selectedCampaigns, this.duplicateResolution, toBeExported, this.icpIntelJobUUID)
        .pipe(untilDestroyed(this))
        .subscribe((failedContactsArray: any) => {
            failedContactsArray.forEach((contact) => {
                this.duplicateContactsMatchIds.forEach((contacts) => {
                    if (contact.matchId === contacts.match_id) {
                        const dFailedContact = new FailedContactModel();
                        dFailedContact.contactName = contact.name ? contact.name : this.getByMatchId(contact.matchId, 'name');
                        dFailedContact.companyName = contact.companyName ? contact.companyName : this.getByMatchId(contact.matchId, 'companyName');
                        dFailedContact.matchId = contact.matchId;
                        this.commonUtilService.removeMatchId(toBeExported, contact);
                        this.duplicateFailedContacts.push(dFailedContact);
                    }
                });
                const statusCode = Number(contact.statusCode);
                // Treat any HTTP status code in the error range (4xx or 5xx) as a failure
                if (!isNaN(statusCode) && statusCode >= 400) {
                    const failedContact = new FailedContactModel();
                    failedContact.contactName = contact.contactName ? contact.contactName : this.getByMatchId(contact.matchId, 'contactName');
                    failedContact.companyName = contact.companyName ? contact.companyName : this.getByMatchId(contact.matchId, 'companyName');
                    failedContact.matchId = contact.matchId;
                    failedContact.errorMessage = contact.message;
                    this.commonUtilService.removeMatchId(toBeExported, contact);
                    this.failedContacts.push(failedContact);
                }
            });

            this.exportedContactCount = toBeExported.length;
            this.isBusy = false;
            this.onNext();
            if (toBeExported.length > 0) {
                this.requestBodyService.changeRevealMatchesID({ 'type': 'dynamics', 'data': toBeExported });
            }
        }, (error) => {
            const errMsg = getErrorMessage(error, true);
            if (errMsg){
                this.exportError = errMsg;
                this.showRetryButton = true;
            } else {
                this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
            }
            this.currentPage = this.pageArray.exportFailed;
            this.showNextButton = false;
            this.showPreviousButton = false;
            this.isBusy = false;
        });
    }

    onRetryExport() {
        if (this.data['contacts']) {
            this.currentPage = this.pageArray.export;
            this.executeCurrentStep();
        } else if (this.data['company']) {
            this.currentPage = this.pages.export;
            this.executeCurrentStepCompany();
        }
    }
}

export class FailedContactModel {
    matchId: string;
    contactName: string;
    companyName: string;
    errorMessage: string;
}
